import axios from 'axios'
import { Message } from 'view-design'
import Cookies from 'js-cookie'
import { sm2 } from 'sm-crypto'
import CryptoJS from 'crypto-js'

// ============================================================
// 配置
// ============================================================
const SM2_PRIVATE_KEY = '19809439c9053a9dbffe4f81780b224ae7aa5ca28dbdd9c26757e12e06121e8c'
const SM2_DECRYPT_KEY = '9ffd2fd68efe551441a93eacfb3f48109eed1710f014519e5c1d67dfbaade5ed'
const TOKEN_VERIFY_CACHE_DURATION = 5 * 60 * 1000

// 真正的公共接口：后端已 SKIP_SECURITY_PATHS 豁免，无需签名头，请求前绝不触发 token 获取。
const PUBLIC_PATHS = [
  '/get/token',
  '/verify/token',
  '/v1/get/token',
  '/v1/verify/token',
]

// 登录类接口：后端仍要求安全请求头（SM2 签名 + X-User-Code 等），
// 但请求前不能触发 ensureValidToken（登录时还没票据，空 token 签名即可，对齐移动端 NO_TOKEN_PATHS）。
// 注意：仅限「拿到 login_code 之前」的接口（登录/注册/发验证码）。
// 登录之后的接口（如 /authorization/）需要真实 token，绝不能进这里，否则不会触发 /get/token 换票。
const NO_TOKEN_PATHS = [
  '/m_api/login/',      // 登录接口
  '/wxlogin/',          // 微信登录
  '/m_api/sendcode/',   // 短信验证码发送
  '/m_api/register/',   // 注册
]

// ============================================================
// 工具函数
// ============================================================

function generateNonce() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16)
  })
}

function stringifyObjectValues(obj) {
  if (!obj) return {}
  const result = {}
  for (const key in obj) {
    if (!obj.hasOwnProperty(key)) continue
    const value = obj[key]
    if (value === null || value === undefined) continue
    if (typeof value === 'object') {
      result[key] = JSON.stringify(value)
    } else {
      result[key] = String(value)
    }
  }
  return result
}

function generateSM2Signature(data) {
  const stringified = stringifyObjectValues(data)
  return sm2.doSignature(JSON.stringify(stringified), SM2_PRIVATE_KEY)
}

function generateSign(timestamp, params, userCode, nonce, token) {
  const signString = timestamp + params + userCode + nonce
  return CryptoJS.HmacSHA256(signString, token || '').toString()
}

function getUserCode() {
  const userInfo = sessionStorage.getItem('userInfo')
  if (userInfo) {
    try {
      const info = JSON.parse(userInfo)
      // 优先用 username（与后端 mg login 签发 login_ticket 的 user_code 一致）
      if (info.username) return String(info.username)
      if (info.user_id) return String(info.user_id)
    } catch (e) { /* fallthrough */ }
  }
  // 未登录时使用 web_admin + UUID，避免多用户共享同一 userCode 导致 IP 校验冲突
  let anonCode = sessionStorage.getItem('anonUserCode')
  if (!anonCode) {
    anonCode = 'web_admin_' + generateNonce().replace(/-/g, '')
    sessionStorage.setItem('anonUserCode', anonCode)
  }
  return anonCode
}

// 登录成功后调用：存储用户信息并清除旧 token（userCode 变化需要重新获取 token）
function setUserInfo(info) {
  sessionStorage.setItem('userInfo', JSON.stringify(info))
  // 清除旧 token 和 anonUserCode，下次请求会用新的 user_id 重新获取
  sessionStorage.removeItem('apiToken')
  sessionStorage.removeItem('token_user_code')
  sessionStorage.removeItem('anonUserCode')
  // 不清 login_code（新登录签发的票据保留，供 fetchNewToken 换 token）
  tokenVerifyCache = { token: null, valid: false, timestamp: 0 }
  console.log('✅ setUserInfo: userCode 已变更为', info.user_id || info.username, '，旧 token 已清除')
}

// 清除登录状态（退出登录 / token 失效时调用）
function clearLoginState() {
  sessionStorage.removeItem('userInfo')
  sessionStorage.removeItem('apiToken')
  sessionStorage.removeItem('token_user_code')
  sessionStorage.removeItem('anonUserCode')
  sessionStorage.removeItem('sbid')
  sessionStorage.removeItem('login_code')
  tokenVerifyCache = { token: null, valid: false, timestamp: 0 }
  // 清除 cookie 中的 auth_key
  Cookies.remove('auth_key')
}

// ============================================================
// Token 管理
// ============================================================

let tokenVerifyCache = { token: null, valid: false, timestamp: 0 }

async function verifyToken(token, baseUrl) {
  if (tokenVerifyCache.token === token && tokenVerifyCache.timestamp > Date.now() - TOKEN_VERIFY_CACHE_DURATION) {
    return { valid: tokenVerifyCache.valid, data: null }
  }
  try {
    const res = await axios.post(baseUrl + '/verify/token', { token },
      { headers: { 'Content-Type': 'application/json' } })
    const valid = res.data && res.data.code === 200
    tokenVerifyCache = { token, valid, timestamp: Date.now() }
    return { valid, data: valid ? res.data.data : null }
  } catch (e) {
    console.warn('验证 token 失败:', e.message)
    return { valid: false, data: null }
  }
}

async function fetchNewToken(userCode, baseUrl) {
  // 对齐移动端 request.js：优先用登录成功后签发的 login_code 票据换正式 token。
  const loginCode = sessionStorage.getItem('login_code') || ''
  const ticketUserCode = loginCode || userCode
  try {
    const res = await axios.post(baseUrl + '/get/token',
      { user_code: ticketUserCode, timestamp: Date.now() },
      { headers: { 'Content-Type': 'application/json' } })
    if (res.data && res.data.code === 200 && res.data.data && res.data.data.token) {
      const newToken = res.data.data.token
      sessionStorage.setItem('apiToken', newToken)
      sessionStorage.setItem('token_user_code', userCode)
      tokenVerifyCache = { token: null, valid: false, timestamp: 0 }
      // 票据已成功换到 token，作废（一次性凭证）
      if (loginCode) sessionStorage.removeItem('login_code')
      console.log('✅ 新 token 已保存（userCode: ' + userCode + '，' + (loginCode ? '经 login_code 票据换取' : '直接 userCode 换取') + '）')
      return newToken
    }
    console.warn('/get/token 返回无效:', res.data)
    return null
  } catch (e) {
    console.error('获取 token 失败:', e.message)
    return null
  }
}

async function ensureValidToken(baseUrl) {
  let token = sessionStorage.getItem('apiToken')
  const userCode = getUserCode()

  // 检测 userCode 是否变化：token 对应的 userCode 与当前 userCode 不一致
  const tokenUserCode = sessionStorage.getItem('token_user_code')
  if (token && tokenUserCode && userCode && tokenUserCode !== userCode) {
    console.log('⚠️ userCode 已变化（' + tokenUserCode + ' → ' + userCode + '），清除旧 token 重新获取')
    sessionStorage.removeItem('apiToken')
    sessionStorage.removeItem('token_user_code')
    // 身份变化时旧票据一并不再有效，避免 login_code 错配到新身份
    sessionStorage.removeItem('login_code')
    token = null
    tokenVerifyCache = { token: null, valid: false, timestamp: 0 }
  }

  if (token) {
    const verifyResult = await verifyToken(token, baseUrl)
    if (!verifyResult.valid) {
      token = null
    }
  }

  if (!token && userCode) {
    token = await fetchNewToken(userCode, baseUrl)
  }

  return token
}

// ============================================================
// 敏感数据 UUID 还原
// ============================================================

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function collectUuids(obj, uuids = []) {
  if (typeof obj === 'string' && UUID_REGEX.test(obj)) {
    if (!uuids.includes(obj)) uuids.push(obj)
  } else if (Array.isArray(obj)) {
    obj.forEach(item => collectUuids(item, uuids))
  } else if (obj && typeof obj === 'object') {
    Object.values(obj).forEach(val => collectUuids(val, uuids))
  }
  return uuids
}

function replaceUuids(obj, replaceMap) {
  if (typeof obj === 'string' && replaceMap.has(obj)) return replaceMap.get(obj)
  if (Array.isArray(obj)) return obj.map(item => replaceUuids(item, replaceMap))
  if (obj && typeof obj === 'object') {
    const result = {}
    for (const [k, v] of Object.entries(obj)) result[k] = replaceUuids(v, replaceMap)
    return result
  }
  return obj
}

async function revealSensitiveData(data, baseUrl) {
  if (!data || typeof data !== 'object') return data

  const uuidList = collectUuids(data)
  if (uuidList.length === 0) return data

  // 一次性 POST 批量请求所有 UUID
  try {
    const res = await axios.post(baseUrl + '/v1/sensitive/reveal', { uuids: uuidList }, {
      timeout: 10000,
      headers: { 'Content-Type': 'application/json' }
    })

    const replaceMap = new Map()
    if (res.data && res.data.code === 0 && res.data.data && res.data.data.results) {
      const results = res.data.data.results
      for (const [uuid, value] of Object.entries(results)) {
        if (value !== null && value !== undefined) {
          replaceMap.set(uuid, value)
        }
      }
    }

    return replaceMap.size > 0 ? replaceUuids(data, replaceMap) : data
  } catch (e) {
    console.warn('批量 UUID 还原失败:', e.message)
    return data
  }
}

// ============================================================
// 主类
// ============================================================

class HttpRequest {
  constructor(baseUrl) {
    this.baseUrl = baseUrl
    this.queue = {}
  }

  destroy(url) {
    delete this.queue[url]
  }

  interceptors(instance, url) {
    instance.interceptors.request.use(async config => {
      if (!Object.keys(this.queue).length) { /* loading */ }
      this.queue[url] = true

      const isPublic = PUBLIC_PATHS.some(p => config.url && config.url.includes(p))
      const isNoToken = NO_TOKEN_PATHS.some(p => config.url && config.url.includes(p))
      console.log('=== Web 签名调试 ===')
      console.log('URL:', config.url)
      console.log('Method:', config.method)
      console.log('isPublic:', isPublic)
      console.log('isNoToken:', isNoToken)
      if (isPublic) return config

      // 登录类接口：不前置获取 token（还没票据），空 token 直接参与签名，但要带安全请求头
      const token = isNoToken ? '' : await ensureValidToken(this.baseUrl)
      const userCode = getUserCode()
      const timestamp = Date.now().toString()
      const nonce = generateNonce()

      const requestData = config.method === 'get' ? (config.params || {}) : (config.data || {})
      const sortedKeys = Object.keys(requestData).sort()
      const sortedData = {}
      sortedKeys.forEach(key => {
        const val = requestData[key]
        if (val === null || val === undefined || val === '') return
        if (typeof val === 'boolean') {
          sortedData[key] = val ? 'true' : 'false'
        } else if (typeof val === 'object') {
          sortedData[key] = JSON.stringify(val)
        } else {
          sortedData[key] = String(val)
        }
      })
      var paramsStr = Object.keys(sortedData).length > 0 ? JSON.stringify(sortedData) : ''

      if (paramsStr === '') {
        paramsStr = '{}'
      }

      const sm2Sign = generateSM2Signature(requestData)
      const hmacSign = generateSign(timestamp, paramsStr, userCode, nonce, token)

      console.log('paramsStr:', paramsStr)
      console.log('signString:', timestamp + paramsStr + userCode + nonce)
      console.log('token:', token ? token.substring(0, 8) + '...' : '空')
      console.log('X-Timestamp:', timestamp)
      console.log('X-Nonce:', nonce)
      console.log('X-User-Code:', userCode)
      console.log('sign (SM2):', sm2Sign ? sm2Sign.substring(0, 20) + '...' : '空')
      console.log('X-Sign (HMAC):', hmacSign)
      console.log('=== 签名调试结束 ===')

      config.headers = config.headers || {}
      config.headers['sign'] = sm2Sign
      config.headers['X-Token'] = token || ''
      config.headers['X-Timestamp'] = timestamp
      config.headers['X-Nonce'] = nonce
      config.headers['X-User-Code'] = userCode
      config.headers['X-Sign'] = hmacSign
      // Web 端登录后的 auth_key(JWT) 需通过 X-Xsrftoken 请求头一并发送（对齐后端区分 Web/小程序）
      config.headers['X-Xsrftoken'] = Cookies.get('auth_key') || ''

      return config
    }, error => Promise.reject(error))

    instance.interceptors.response.use(async res => {
      this.destroy(url)

      let datares = res.data

      if (typeof datares === 'string') {
        try {
          const decryptData = sm2.doDecrypt(datares, SM2_DECRYPT_KEY, 1)
          datares = JSON.parse(decryptData)
        } catch (e) {
          // 不是 SM2 加密数据，保持原样
        }
      }

      if (datares && typeof datares === 'object' && datares.data) {
        try {
          datares.data = await revealSensitiveData(datares.data, this.baseUrl)
        } catch (e) {
          console.warn('UUID 还原失败:', e.message)
        }
      }

      res.data = datares
      const { data, status, request } = res
      return { data, status, request }
    }, error => {
      this.destroy(url)
      if (error.response) {
        const code = error.response.status
        const errData = error.response.data
        const errMsg = (typeof errData === 'object' && errData.message) || (typeof errData === 'string' && errData) || ''

        if (code === 401) {
          const isKicked = errMsg && (
            errMsg.includes('异地登录') ||
            errMsg.includes('在其他地方登录')
          )
          const isIPMismatch = errMsg && errMsg.includes('IP地址不匹配')
          const isUserCodeMismatch = errMsg && errMsg.includes('用户码与Token不匹配')

          if (isKicked || isIPMismatch) {
            Message.warning({ content: '您的账号在其他地方登录，当前会话已失效', duration: 5, closable: true })
          } else if (isUserCodeMismatch) {
            Message.warning({ content: '用户身份验证失败，请重新登录', duration: 5, closable: true })
          } else {
            Message.warning({ content: '登录已过期，请重新登录', duration: 5, closable: true })
          }
          clearLoginState()
          setTimeout(() => { location.reload() }, 1500)
        } else if (code === 403) {
          Message.error({ content: '签名验证失败或无权限访问', duration: 8, closable: true })
          clearLoginState()
          setTimeout(() => { location.reload() }, 1500)
        } else if (code === 555) {
          Message.error({ content: '安全验证失败，请刷新页面重试', duration: 8, closable: true })
          clearLoginState()
          setTimeout(() => { location.reload() }, 1500)
        } else {
          Message.error({ content: `${code}-错误`, duration: 8, closable: true })
        }
      }
      return Promise.reject(error)
    })
  }

  request(options) {
    const instance = axios.create()
    options.baseURL = this.baseUrl
    this.interceptors(instance, options.url)
    return instance(options)
  }
}

export default HttpRequest
export { setUserInfo, clearLoginState }
